import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatShort } from "@/lib/datetime";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { SynonymForm } from "@/components/search/SynonymSuggest";
import { Check, ChevronRight, Loader2, RotateCcw, X } from "lucide-react";

function SynRow({ s, actions }) {
  return (
    <div data-testid={`syn-row-${s.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-slate-100 first:border-t-0">
      <div className="flex-1 min-w-[12rem]">
        <div className="font-semibold text-slate-900">{s.terms.join(" = ")}</div>
        <div className="text-xs text-slate-500">
          suggested by {s.suggested_by} · {formatShort(s.suggested_at)}
          {s.came_from === "weekly_list" ? " · from the found-nothing list" : ""}
          {s.decided_by && s.status !== "waiting" ? ` · ${s.status === "approved" ? "put in use" : s.status} by ${s.decided_by}` : ""}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">{actions}</div>
    </div>
  );
}

function Section({ testid, title, items, empty, render }) {
  return (
    <section data-testid={testid}>
      <h2 className="text-base md:text-lg font-display font-bold text-slate-800 mb-2">{title} <span className="text-slate-400 font-mono text-sm">({items.length})</span></h2>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {items.length ? items.map(render) : <div data-testid={`${testid}-empty`} className="p-5 text-sm text-slate-400 text-center">{empty}</div>}
      </div>
    </section>
  );
}

export default function SearchSynonymsPage() {
  const [items, setItems] = useState(null);
  const [showOld, setShowOld] = useState(false);
  const load = useCallback(() => {
    api.get("/search-synonyms").then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (s, path, body, msg) => {
    try { await api.post(`/search-synonyms/${s.id}/${path}`, body); toast.success(msg); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const btn = (testid, onClick, Icon, label, tone) => (
    <Button data-testid={testid} variant="outline" onClick={onClick} className={`h-10 rounded-lg text-sm ${tone || ""}`}><Icon className="w-4 h-4 mr-1" />{label}</Button>
  );
  if (!items) return <div className="p-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  const by = (st) => items.filter((s) => st.includes(s.status));
  return (
    <div data-testid="search-synonyms-page" className="max-w-4xl space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Search synonyms</h1>
        <p className="text-sm text-slate-500">Words the crew uses for the same thing. Once in use, searching either word finds both — in Find a Part, All Parts and Vehicles. Scans stay exact.</p>
      </div>
      <div data-testid="syn-add" className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
        <div className="text-sm font-semibold text-slate-700 mb-2">Add one (it goes straight into use)</div>
        <SynonymForm testid="syn-add-form" cameFrom="synonym_page" onDone={load} />
      </div>
      <Section testid="syn-waiting" title="Waiting for approval" items={by(["waiting"])} empty="Nothing is waiting."
        render={(s) => <SynRow key={s.id} s={s} actions={<>
          {btn(`syn-use-${s.id}`, () => act(s, "decide", { decision: "use" }, `In use: ${s.terms.join(" = ")}`), Check, "Use it", "border-emerald-300 text-emerald-700")}
          {btn(`syn-decline-${s.id}`, () => act(s, "decide", { decision: "decline" }, "Declined"), X, "Decline")}
        </>} />} />
      <Section testid="syn-in-use" title="In use" items={by(["approved"])} empty="No synonyms in use yet."
        render={(s) => <SynRow key={s.id} s={s} actions={btn(`syn-retire-${s.id}`, () => act(s, "retire", {}, "Taken out of use"), X, "Take out of use")} />} />
      <button type="button" data-testid="syn-old-toggle" onClick={() => setShowOld((o) => !o)}
        className="inline-flex items-center gap-1.5 h-10 text-sm font-medium text-slate-500 hover:text-slate-800">
        <ChevronRight className={`w-4 h-4 transition-transform ${showOld ? "rotate-90" : ""}`} />Taken out of use or declined ({by(["retired", "declined"]).length})
      </button>
      {showOld && (
        <Section testid="syn-old" title="Taken out of use or declined" items={by(["retired", "declined"])} empty="None."
          render={(s) => <SynRow key={s.id} s={s} actions={s.status === "retired"
            ? btn(`syn-restore-${s.id}`, () => act(s, "restore", {}, `Back in use: ${s.terms.join(" = ")}`), RotateCcw, "Put back")
            : <span data-testid={`syn-declined-${s.id}`} className="text-xs text-slate-400">declined</span>} />} />
      )}
    </div>
  );
}
